import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { OrganizationDisplayProps } from "../utils/types";
import { OrganizationsPopover } from "./OrganizationsPopover";
import { OrganizationsModal } from "./OrganizationsModal";

const calculatePopupPosition = (
  rect: DOMRect,
  viewportHeight: number,
  popupHeight: number,
  popupWidth: number,
) => {
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;

  const x = rect.right - popupWidth;
  let y: number;
  let placement: "below" | "above";

  if (spaceBelow < popupHeight && spaceAbove > spaceBelow) {
    y = rect.top - popupHeight;
    placement = "above";
  } else {
    y = rect.bottom;
    placement = "below";
  }

  return { x, y, placement };
};

const OrganizationDisplay: React.FC<OrganizationDisplayProps> = ({
  organizations,
  containerWidth = 350,
}) => {
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [popupPosition, setPopupPosition] = useState<{
    x: number;
    y: number;
    placement: "below" | "above";
  } | null>(null);
  const [popoverMeasured, setPopoverMeasured] = useState(false);

  const moreButtonRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const popoverRefCallback = useCallback((node: HTMLDivElement | null) => {
    (popoverRef as React.MutableRefObject<HTMLDivElement | null>).current =
      node;
    if (node && moreButtonRef.current) {
      const rect = moreButtonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const popupHeight = node.offsetHeight;
      const popupWidth = node.offsetWidth;
      setPopupPosition(
        calculatePopupPosition(rect, viewportHeight, popupHeight, popupWidth),
      );
      setPopoverMeasured(true);
    }
  }, []);

  const handleViewAll = () => {
    setIsPopoverVisible(false);
    setIsModalOpen(true);
  };

  const visibleCount = useMemo(() => {
    if (!organizations?.length) {
      return 0;
    }

    const moreButtonWidth = 80;
    const iconWidth = 16;
    const gapWidth = 6;
    const commaWidth = 8;
    const padding = 24;
    const avgTextWidth = 70;

    const availableWidth = containerWidth - padding;
    let totalWidth = 0;
    let count = 0;

    for (let i = 0; i < organizations.length; i++) {
      const estimatedTextWidth = Math.min(
        organizations[i]?.client_name?.length * 7 || avgTextWidth,
        100,
      );
      const itemWidth =
        iconWidth + gapWidth + estimatedTextWidth + (i > 0 ? commaWidth : 0);

      const needsMoreButton = i < organizations.length - 1;
      const requiredWidth =
        totalWidth + itemWidth + (needsMoreButton ? moreButtonWidth : 0);

      if (requiredWidth > availableWidth && count > 0) {
        break;
      }

      totalWidth += itemWidth;
      count++;
    }

    return Math.max(1, Math.min(count, organizations.length));
  }, [organizations, containerWidth]);

  const updatePopupPosition = () => {
    if (moreButtonRef.current) {
      const rect = moreButtonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const popupHeight = popoverRef.current?.offsetHeight || 300;
      const popupWidth = popoverRef.current?.offsetWidth || 200;

      setPopupPosition(
        calculatePopupPosition(rect, viewportHeight, popupHeight, popupWidth),
      );
    }
  };

  useEffect(() => {
    if (isPopoverVisible) {
      setPopupPosition({ x: -9999, y: -9999, placement: "below" });
      setPopoverMeasured(false);

      const handleScroll = () => updatePopupPosition();
      const handleResize = () => updatePopupPosition();

      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleResize);
      };
    } else {
      setPopupPosition(null);
      setPopoverMeasured(false);
    }
  }, [isPopoverVisible]);

  if (!organizations?.length) {
    return <span className="text-sm text-gray-400">-</span>;
  }

  const visibleOrgs = organizations.slice(0, visibleCount);
  const remainingCount = Math.max(0, organizations.length - visibleCount);

  return (
    <>
      <div className="relative flex items-center gap-1.5 max-w-full">
        <div className="flex items-center gap-1.5 overflow-hidden">
          {visibleOrgs?.map((org, index) => (
            <div
              key={org?.client_code || index}
              className="flex items-center gap-1.5 shrink-0"
            >
              <img
                src={org?.client_logo}
                alt={org?.client_name}
                className="w-4 h-4 object-cover shrink-0"
              />
              <span
                className="text-sm text-heading-text dark:text-heading-text-dk whitespace-nowrap truncate max-w-[100px]"
                title={org?.client_name}
              >
                {org?.client_name}
                {index < visibleOrgs?.length - 1 && " ,"}
              </span>
            </div>
          ))}
        </div>

        {remainingCount > 0 && (
          <div className="relative shrink-0">
            <span
              ref={moreButtonRef}
              className="text-xxs font-semibold text-verba-indigo dark:text-verba-indigo-dk border border-gray-100 dark:border-gray-100-dk px-1 py-[3.25px] rounded-md whitespace-nowrap ml-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-50-dk transition-colors inline-block"
              onMouseEnter={() => setIsPopoverVisible(true)}
              onMouseLeave={() => setIsPopoverVisible(false)}
            >
              +{remainingCount} More
            </span>

            {isPopoverVisible &&
              popupPosition &&
              createPortal(
                <div
                  ref={popoverRefCallback}
                  className={`fixed z-9999999 ${
                    popupPosition.placement === "below" ? "pt-1" : "pb-1"
                  }`}
                  style={{
                    top: `${popupPosition.y}px`,
                    left: `${popupPosition.x}px`,
                    visibility: popoverMeasured ? "visible" : "hidden",
                  }}
                  onMouseEnter={() => setIsPopoverVisible(true)}
                  onMouseLeave={() => setIsPopoverVisible(false)}
                >
                  <OrganizationsPopover
                    organizations={organizations}
                    onViewAll={handleViewAll}
                  />
                </div>,
                document.body,
              )}
          </div>
        )}
      </div>

      <OrganizationsModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        organizations={organizations}
      />
    </>
  );
};

export default OrganizationDisplay;
